import { Plus } from 'lucide-react';

export default function PlayerCard({ 
  slotNumber, 
  position, 
  player, 
  onClick, 
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging = false,
  isDragOver = false
}) {
  const isEmpty = !player;
  const canDrag = !isEmpty;
  
  const handleClick = (e) => {
    if (e.defaultPrevented) return;
    onClick();
  };

  const handleDragStart = (e) => {
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    // Set drag data to make drag work properly on first attempt
    e.dataTransfer.setData('text/plain', slotNumber.toString());
    e.dataTransfer.effectAllowed = 'move';
    onDragStart();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver(e);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    onDrop(e);
  };
  
  return (
    <div
      className="absolute cursor-pointer transition-all duration-200 hover:scale-105 hover:z-50"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto'
      }}
      onClick={handleClick}
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={onDragEnd}
      data-testid={`player-card-slot-${slotNumber}`}
    >
      {/* Card - Responsivo - Ainda menor no mobile */}
      <div
        className="relative w-16 h-24 sm:w-20 sm:h-28 md:w-24 md:h-36 lg:w-32 lg:h-44 rounded-md sm:rounded-lg md:rounded-xl border-2 transition-all shadow-lg sm:shadow-2xl"
        style={{
          background: isEmpty 
            ? 'rgba(30, 30, 40, 0.7)'
            : player.type === 'bot'
            ? 'linear-gradient(180deg, rgba(60, 60, 70, 0.9) 0%, rgba(40, 40, 50, 0.9) 100%)'
            : 'linear-gradient(180deg, rgba(255, 90, 0, 0.15) 0%, rgba(20, 21, 28, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          borderColor: isDragOver
            ? '#00FF66'
            : isEmpty 
            ? 'rgba(255, 255, 255, 0.15)'
            : player.type === 'bot'
            ? 'rgba(100, 100, 120, 0.4)'
            : '#FF5A00',
          borderStyle: isEmpty ? 'dashed' : 'solid',
          borderWidth: isDragOver ? '3px' : '2px',
          boxShadow: isDragOver
            ? '0 0 30px rgba(0, 255, 102, 0.6)'
            : isEmpty 
            ? 'none'
            : player.type === 'bot'
            ? '0 4px 16px rgba(0, 0, 0, 0.5)'
            : '0 4px 20px rgba(255, 90, 0, 0.4)'
        }}
      >
        {isEmpty ? (
          <div className="h-full flex items-center justify-center">
            <Plus 
              className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 lg:h-10 lg:w-10 text-[#FF5A00] animate-pulse" 
              style={{ filter: 'drop-shadow(0 0 8px rgba(255, 90, 0, 0.6))' }} 
            />
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center p-1 sm:p-1.5 md:p-2 lg:p-3">
            {/* Photo - Responsivo - Menor no mobile */}
            <div className="relative mb-0.5 sm:mb-1 md:mb-1.5 lg:mb-2">
              {player.type === 'real' && player.photo_url ? (
                <img
                  src={player.photo_url}
                  alt={player.game_tag}
                  className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 rounded-full object-cover border border-[#FF5A00] sm:border-2"
                  style={{ boxShadow: '0 0 8px rgba(255, 90, 0, 0.5)' }}
                  draggable={false}
                />
              ) : (
                <img
                  src="https://images.pexels.com/photos/6019019/pexels-photo-6019019.jpeg"
                  alt="Bot"
                  className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 rounded-full object-cover border border-gray-500 sm:border-2 opacity-60"
                  draggable={false}
                />
              )}
            </div>
            
            {/* Info - Responsivo - Texto menor no mobile */}
            <div className="text-center w-full space-y-0">
              <p 
                className="text-white font-bold text-[9px] sm:text-[10px] md:text-xs lg:text-sm truncate stats-font leading-tight"
                style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}
                title={player.type === 'real' ? player.game_tag : 'BOT'}
              >
                {player.type === 'real' ? player.game_tag : 'BOT'}
              </p>
              
              {player.type === 'real' && (
                <>
                  <p className="text-[#A1A1AA] text-[7px] sm:text-[8px] md:text-[9px] lg:text-xs leading-tight" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}>
                    {player.age} anos
                  </p>
                  <p 
                    className="text-[#A1A1AA] text-[7px] sm:text-[8px] md:text-[9px] lg:text-xs truncate leading-tight" 
                    style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}
                    title={player.state}
                  >
                    {player.state}
                  </p>
                </>
              )}
              
              {player.type === 'bot' && (
                <>
                  <p className="text-gray-400 text-[7px] sm:text-[8px] md:text-[9px] lg:text-xs">N/A</p>
                  <p className="text-gray-400 text-[7px] sm:text-[8px] md:text-[9px] lg:text-xs">N/A</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Position Label - Orange Circle Below - Menor no mobile */}
      <div 
        className="absolute -bottom-7 sm:-bottom-8 md:-bottom-9 lg:-bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
      >
        <div 
          className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center"
          style={{
            background: isDragOver 
              ? 'linear-gradient(135deg, #00AA44 0%, #00FF66 100%)'
              : 'linear-gradient(135deg, #c2410c 0%, #FF5A00 100%)',
            boxShadow: isDragOver
              ? '0 4px 16px rgba(0, 255, 102, 0.7)'
              : '0 4px 12px rgba(255, 90, 0, 0.5)',
            border: '2px solid rgba(255, 150, 100, 0.3)',
            transition: 'all 0.2s'
          }}
        >
          <p 
            className="text-white font-bold text-[8px] sm:text-[9px] md:text-[10px] lg:text-sm stats-font"
            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
          >
            {position.label}
          </p>
        </div>
      </div>
    </div>
  );
}
